import React, { useEffect, useMemo, useState } from 'react';
import { useTemporada } from '../../context/TemporadaContext';
import { useAuth } from '../../context/AuthContext';
import { descargarActaPDF } from '../../utils/descargaActa';
import { nombreVisible } from '../../utils/jugadores';
import { useCalendarioDetalle } from '../../hooks/useCalendarioDetalle';
import { getPistasPublicadas, getDatosJornadas } from '../../services/asignacionPistaService';
import { derivarFranjas, mapearPartidos, computeConflictos } from './logica';
import FilaFranja from './FilaFranja';
import { AplazadosDeJornada, aplazadosDeJornada } from './AplazadosJustificados';
import './CompeticionPistas.css';

const PISTAS = [1, 2, 3, 4];

// Jugadores (individual o los dos del equipo de dobles, de ambos lados) de un partido del calendario.
const jugadoresDeAplazado = (p, roster) => [
  ...(roster[p.local_participante_id] || []),
  ...(roster[p.visitante_participante_id] || []),
];

// Mismo corte que el CSS móvil de CompeticionPistas.css.
const CONSULTA_MOVIL = '(max-width: 640px)';
const useEsMovil = () => {
  const [esMovil, setEsMovil] = useState(() => !!window.matchMedia && window.matchMedia(CONSULTA_MOVIL).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(CONSULTA_MOVIL);
    const alCambiar = (e) => setEsMovil(e.matches);
    setEsMovil(mq.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);
  return esMovil;
};

// Vista pública de solo lectura (sin login). Solo muestra la jornada real
// seleccionada si el ADMIN la ha publicado — ver docs/PLAN_COMPETICION_PISTAS.md §2.1/§2.7.
const CompeticionPistasPublico = () => {
  const { temporada } = useTemporada();
  const { usuario } = useAuth();
  const { partidos: calendario, rosterPorParticipante, cargando: cargandoCalendario } = useCalendarioDetalle(temporada);

  // Una jornada real (temporada_id + jornada) puede reunir IDA de una
  // modalidad y VUELTA de otra — ver CompeticionPistas.js y badminton-api
  // docs/DOCUMENTACION_BD.md §2.
  const jornadasReales = useMemo(() => {
    const vistos = new Set();
    calendario.forEach((p) => vistos.add(p.jornada));
    return [...vistos].sort((a, b) => a - b).map((jornada) => ({ jornada }));
  }, [calendario]);

  const [jornadaSel, setJornadaSel] = useState(null);
  useEffect(() => {
    if (jornadasReales.length && (!jornadaSel || !jornadasReales.some((j) => j.jornada === jornadaSel.jornada))) {
      setJornadaSel(jornadasReales[0]);
    }
  }, [jornadasReales, jornadaSel]);

  // Fecha ('YYYY-MM-DD') de cada jornada, para mostrarla en el selector.
  const [fechasJornada, setFechasJornada] = useState({});
  useEffect(() => {
    if (!temporada) return;
    getDatosJornadas(temporada)
      .then((datos) => {
        const mapa = {};
        (datos || []).forEach((d) => { if (d.fecha) mapa[d.jornada] = d.fecha; });
        setFechasJornada(mapa);
      })
      .catch(() => setFechasJornada({}));
  }, [temporada]);
  const formatearFecha = (iso) => iso.slice(0, 10).split('-').reverse().join('/');

  const [datosPistas, setDatosPistas] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!temporada || !jornadaSel) return;
    setCargando(true);
    getPistasPublicadas(temporada, jornadaSel.jornada)
      .then(setDatosPistas)
      .catch(() => setDatosPistas(null))
      .finally(() => setCargando(false));
  }, [temporada, jornadaSel]);

  const franjas = useMemo(() => (datosPistas ? derivarFranjas(datosPistas.disponibilidad) : []), [datosPistas]);
  const partidos = useMemo(
    () => (datosPistas ? mapearPartidos(datosPistas.partidos, rosterPorParticipante) : []),
    [datosPistas, rosterPorParticipante],
  );
  // Aplazados y WO de la jornada: no ocupan pista. Si siguen en el cuadrante publicado se indica dónde.
  const aplazadosPropios = useMemo(
    () => (jornadaSel ? aplazadosDeJornada(calendario, jornadaSel.jornada) : []),
    [calendario, jornadaSel],
  );
  const esMovil = useEsMovil();

  // El acta lleva motivos de incidencia (p. ej. lesión) que no se publican: solo gestión.
  const puedeDescargarActa = usuario?.rol === 'ADMIN' || usuario?.rol === 'COMITE';
  const [errorActa, setErrorActa] = useState(null);
  const descargarActa = () => {
    setErrorActa(null);
    descargarActaPDF(calendario, temporada, jornadaSel.jornada, datosPistas.partidos, `acta_${temporada}_jornada-${jornadaSel.jornada}.pdf`)
      .catch(() => setErrorActa(`No se pudo generar el acta de la jornada ${jornadaSel.jornada}.`));
  };

  // Jugadores del cuadrante publicado (clave: nombre completo; se muestra el alias). En móvil
  // también los de los aplazados/WO, porque ahí el jugador filtra esa sección.
  const jugadoresOpciones = useMemo(() => {
    const mapa = new Map();
    partidos.forEach((p) => p.jugadoresVisibles.forEach((j) => mapa.set(j.nombre, j.visible)));
    if (esMovil) {
      aplazadosPropios.forEach((p) => jugadoresDeAplazado(p, rosterPorParticipante)
        .forEach((j) => mapa.set(j.nombre, nombreVisible(j))));
    }
    return [...mapa].sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [partidos, esMovil, aplazadosPropios, rosterPorParticipante]);
  const [jugadorSel, setJugadorSel] = useState('');
  useEffect(() => {
    if (jugadorSel && !jugadoresOpciones.some(([nombre]) => nombre === jugadorSel)) setJugadorSel('');
  }, [jugadoresOpciones, jugadorSel]);

  // En móvil, elegir un jugador filtra el cuadrante a sus partidos (dobles o individual);
  // en escritorio solo resalta en amarillo sus celdas.
  const filtrando = esMovil && !!jugadorSel;
  const partidosVisibles = useMemo(
    () => (filtrando ? partidos.filter((p) => p.jugadores.includes(jugadorSel)) : partidos),
    [filtrando, partidos, jugadorSel],
  );
  const franjasVisibles = useMemo(
    () => (filtrando ? franjas.filter((f) => partidosVisibles.some((p) => p.franjaId === f.id)) : franjas),
    [filtrando, franjas, partidosVisibles],
  );

  const conflictosPorCelda = useMemo(() => computeConflictos(partidos), [partidos]);
  const partidosPorCelda = useMemo(() => {
    const mapa = {};
    partidosVisibles.forEach((p) => { if (p.franjaId) mapa[`${p.pista}-${p.franjaId}`] = p; });
    return mapa;
  }, [partidosVisibles]);

  // En móvil, con un jugador elegido solo se listan los aplazados/WO en los que juega.
  const aplazadosVisibles = useMemo(
    () => (filtrando
      ? aplazadosPropios.filter((p) => jugadoresDeAplazado(p, rosterPorParticipante).some((j) => j.nombre === jugadorSel))
      : aplazadosPropios),
    [filtrando, aplazadosPropios, rosterPorParticipante, jugadorSel],
  );
  const aplazadosColocados = useMemo(() => {
    const ids = new Set(aplazadosPropios.map((p) => p.id));
    const mapa = {};
    partidos.forEach((p) => { if (p.franjaId && ids.has(p.id)) mapa[p.id] = { pista: p.pista, franjaId: p.franjaId }; });
    return mapa;
  }, [partidos, aplazadosPropios]);

  // Ordenador/tablet: a la derecha de los selectores. Móvil: al final de la página (el CSS
  // muestra uno u otro).
  const botonActa = (clase) => puedeDescargarActa && jornadaSel && (
    <div className={`cp-acta ${clase}`}>
      <button
        type="button"
        className="cp-btn-acta"
        onClick={descargarActa}
        disabled={!datosPistas}
        title={datosPistas ? 'Descargar el acta de la jornada' : 'El acta necesita la jornada publicada'}
      >
        Acta
      </button>
      {errorActa && <span role="alert" className="cp-acta-error">{errorActa}</span>}
    </div>
  );

  return (
    <div className="competicion-pistas competicion-pistas-publico">
      <div className="page-header"><h2>Pistas de la jornada</h2></div>

      <div className="cp-barra-filtros">
        <label className="cp-jornada-selector">
          Jornada
          <select
            value={jornadaSel ? jornadaSel.jornada : ''}
            onChange={(e) => setJornadaSel({ jornada: Number(e.target.value) })}
          >
            {jornadasReales.map((j) => (
              <option key={j.jornada} value={j.jornada}>
                Jornada {j.jornada}{fechasJornada[j.jornada] ? ` · ${formatearFecha(fechasJornada[j.jornada])}` : ''}
              </option>
            ))}
          </select>
        </label>

        {datosPistas && (
          <label className="cp-jornada-selector">
            Jugador
            <select value={jugadorSel} onChange={(e) => setJugadorSel(e.target.value)}>
              <option value="">— Ninguno —</option>
              {jugadoresOpciones.map(([nombre, visible]) => (
                <option key={nombre} value={nombre}>{visible}</option>
              ))}
            </select>
          </label>
        )}

        {botonActa('cp-acta-escritorio')}
      </div>

      {(cargandoCalendario || cargando) && <p>Cargando…</p>}

      {!cargandoCalendario && !cargando && !datosPistas && (
        <p className="cp-sin-publicar">Todavía no se ha publicado la distribución de pistas de esta jornada.</p>
      )}

      {datosPistas && (
        <div className="tabla-wrapper">
          <table className={`cuadrante-pistas${filtrando ? ' cuadrante-filtrado' : ''}`}>
            <thead>
              <tr>
                <th scope="col">Hora</th>
                {PISTAS.map((pista) => <th key={pista} scope="col">Pista {pista}</th>)}
              </tr>
            </thead>
            <tbody>
              {franjasVisibles.map((franja) => (
                <FilaFranja
                  key={franja.id}
                  franja={franja}
                  pistas={PISTAS}
                  partidosPorCelda={partidosPorCelda}
                  conflictosPorCelda={conflictosPorCelda}
                  editable={false}
                  jugadorResaltado={filtrando ? '' : jugadorSel}
                  onDropPartido={() => {}}
                  onMoverFranja={() => {}}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!cargandoCalendario && (
        <AplazadosDeJornada propios={aplazadosVisibles} colocados={aplazadosColocados} onSacar={null} procesando={false} />
      )}

      {botonActa('cp-acta-movil')}
    </div>
  );
};

export default CompeticionPistasPublico;
